import {
  NotificationPolicyTypeEnum,
  NotificationStatus,
  NotificationType,
} from '@repo/api-gw-sdk';
import type { StaticImageData } from 'next/image';

import { getDocsUrl } from '@/docs/docsUrls';

import Datadog from '../../../public/statics/images/apps/datadog.png';
import Email from '../../../public/statics/images/logos/email-icon.png';
import SNS from '../../../public/statics/images/notifications/Amazon SNS.png';
import ServiceNow from '../../../public/statics/images/notifications/ServiceNow.png';
import Webhook from '../../../public/statics/images/notifications/Webhook.png';
import Slack from '../../../public/statics/images/notifications/slack.png';
import Splunk from '../../../public/statics/images/notifications/splunk.png';

const notificationTypes: Record<NotificationType, { title: string }> = {
  [NotificationType.NotificationTypeUnknown]: {
    title: 'Unknown',
  },
  [NotificationType.BackupJobFailed]: {
    title: 'Backup job failed',
  },
  [NotificationType.BackupJobRejected]: {
    title: 'Backup job rejected',
  },
  [NotificationType.BackupJobSkipped]: {
    title: 'Backup job skipped',
  },
  [NotificationType.RestoreJobFailed]: {
    title: 'Restore job failed',
  },
  [NotificationType.RestoreJobRejected]: {
    title: 'Restore job rejected',
  },
  [NotificationType.ClassificationJobFailed]: {
    title: 'Classification job failed',
  },
  [NotificationType.ClassificationJobRejected]: {
    title: 'Classification job rejected',
  },
  [NotificationType.ClassificationJobSkipped]: {
    title: 'Classification job skipped',
  },
  [NotificationType.ClassificationPolicyCreated]: {
    title: 'Classification policy created',
  },
  [NotificationType.ClassificationPolicyUpdated]: {
    title: 'Classification policy updated',
  },
  [NotificationType.ClassificationPolicyDeleted]: {
    title: 'Classification policy deleted',
  },
  [NotificationType.ClassificationPolicyApplied]: {
    title: 'Resource assigned to classification policy',
  },
  [NotificationType.ClassificationPolicyUnapplied]: {
    title: 'Resource removed from classification policy',
  },
  [NotificationType.RestoreJobSucceeded]: {
    title: 'Restore job completed',
  },
  [NotificationType.BackupJobSucceeded]: {
    title: 'Backup job completed',
  },
  [NotificationType.SnapshotUserProtected]: {
    title: 'Snapshot locked from deletion',
  },
  [NotificationType.SnapshotUserUnprotected]: {
    title: 'Snapshot unlocked from deletion',
  },
  [NotificationType.BackupPolicyApplied]: {
    title: 'Resource assigned to backup policy',
  },
  [NotificationType.BackupPolicyUnapplied]: {
    title: 'Resource removed from backup policy',
  },
  [NotificationType.BackupPolicyCreated]: {
    title: 'Backup policy created',
  },
  [NotificationType.BackupPolicyUpdated]: {
    title: 'Backup policy updated',
  },
  [NotificationType.BackupPolicyDeleted]: {
    title: 'Backup policy deleted',
  },
  [NotificationType.BackupVaultCreated]: {
    title: 'Vault created',
  },
  [NotificationType.BackupVaultDeleted]: {
    title: 'Vault deleted',
  },
  [NotificationType.CloudAccountCreated]: {
    title: 'Cloud account connected',
  },
  [NotificationType.CloudAccountUpdated]: {
    title: 'Cloud account updated',
  },
  [NotificationType.CloudAccountDeleted]: {
    title: 'Cloud account deleted',
  },
  [NotificationType.CloudAccountDeactivated]: {
    title: 'Cloud account deactivated',
  },
  [NotificationType.CloudAccountActivated]: {
    title: 'Cloud account activated',
  },
  [NotificationType.RansomwareDetectedInResource]: {
    title: 'Ransomware detected in resource',
  },
  [NotificationType.RansomwareDetectedInSnapshot]: {
    title: 'Ransomware detected in snapshot',
  },
  [NotificationType.RansomwareInfectionRemediated]: {
    title: 'Ransomware infection remediated',
  },
  [NotificationType.ResourceExcludedFromRansomwareDetection]: {
    title: 'Resource excluded from ransomware detection',
  },
  [NotificationType.RansomwareDetectionExclusionCanceled]: {
    title: 'Ransomware detection exclusion canceled for resource',
  },
  [NotificationType.ViolationsDetectedOnResources]: {
    title: 'Compliance violations detected on resources',
  },
  [NotificationType.ViolationsResolvedOnResources]: {
    title: 'Compliance violations resolved on resources',
  },
  [NotificationType.IssueMutedOnResource]: {
    title: 'Issue muted on resource',
  },
  [NotificationType.IssueUnmutedOnResource]: {
    title: 'Issue unmuted on resource',
  },
  [NotificationType.AuditLogCreated]: {
    title: 'Audit log created',
  },
  [NotificationType.JobMetricsReportFailed]: {
    title: 'Job metrics report failed',
  },
  [NotificationType.CreditsUsageThreshold75]: {
    title: 'Credit pack usage reached 75%',
  },
  [NotificationType.CreditsUsageThreshold90]: {
    title: 'Credit pack usage reached 90%',
  },
  [NotificationType.CreditsUsageThreshold95]: {
    title: 'Credit pack usage reached 95%',
  },
  [NotificationType.CreditsMonthlyUsage80]: {
    title: 'Planned monthly credit usage reached 80%',
  },
  [NotificationType.CreditsMonthlyUsage90]: {
    title: 'Planned monthly credit usage reached 90%',
  },
  [NotificationType.CreditsMonthlyUsage100]: {
    title: 'Planned monthly credit usage reached 100%',
  },
  [NotificationType.MalwareDetectedInSnapshot]: {
    title: 'Malware detected in snapshot',
  },
  [NotificationType.DataAnomalyDetectedInSnapshot]: {
    title: 'Data anomaly detected in snapshot',
  },
  [NotificationType.MalwareRemediated]: {
    title: 'Malware infection remediated',
  },
  [NotificationType.DataAnomalyRemediated]: {
    title: 'Data anomaly resolved',
  },
  [NotificationType.AiDataRiskDetectedInSnapshot]: {
    title: 'AI data risk detected in snapshot',
  },
  [NotificationType.DataExposureRiskDetectedInSnapshot]: {
    title: 'Data exposure risk detected in snapshot',
  },
  [NotificationType.AiDataRiskRemediated]: {
    title: 'AI data risk resolved',
  },
  [NotificationType.DataExposureRiskRemediated]: {
    title: 'Data exposure risk resolved',
  },
  [NotificationType.SnapshotIncludesAccessDeniedItems]: {
    title: 'Snapshot completed with access denied items',
  },
  [NotificationType.MpaRequestPendingApproval]: {
    title: 'Action request pending approval',
  },
  [NotificationType.MpaRequestApproved]: {
    title: 'Action request approved',
  },
  [NotificationType.MpaRequestDenied]: {
    title: 'Action request denied',
  },
  [NotificationType.MpaRequestPartiallyApproved]: {
    title: 'Action request partially approved',
  },
  [NotificationType.MpaRequestExecuted]: {
    title: 'Action request applied',
  },
  [NotificationType.MpaRequestExpired]: {
    title: 'Action request expired',
  },
  [NotificationType.MpaRequestCancelled]: {
    title: 'Action request canceled',
  },
  [NotificationType.GdsgSlaBreached]: {
    title: 'DSG SLA breached',
  },
  [NotificationType.GdsgSystemInaccessible]: {
    title: 'DSG system inaccessible',
  },
  [NotificationType.GdsgWorkerFailure]: {
    title: 'DSG worker failure',
  },
};

export const NotificationTypes = Object.fromEntries(
  Object.entries(notificationTypes)
);

const notificationStatuses: Record<NotificationStatus, { title: string }> = {
  [NotificationStatus.Statussent]: {
    title: 'Sent',
  },
  [NotificationStatus.Statusfailed]: {
    title: 'Failed',
  },
  [NotificationStatus.Statuspending]: {
    title: 'Pending',
  },
};

export const NotificationStatuses = Object.fromEntries(
  Object.entries(notificationStatuses)
);

export const supportedNotificationTypes = [
  NotificationType.RestoreJobSucceeded,
  NotificationType.RestoreJobFailed,
  NotificationType.RestoreJobRejected,
  NotificationType.BackupJobFailed,
  NotificationType.BackupJobRejected,
  NotificationType.BackupJobSkipped,
  NotificationType.BackupJobSucceeded,
  NotificationType.BackupPolicyApplied,
  NotificationType.BackupPolicyUnapplied,
  NotificationType.BackupPolicyCreated,
  NotificationType.BackupPolicyUpdated,
  NotificationType.BackupPolicyDeleted,
  NotificationType.BackupVaultCreated,
  NotificationType.BackupVaultDeleted,
  NotificationType.CloudAccountCreated,
  NotificationType.CloudAccountDeactivated,
  NotificationType.CloudAccountActivated,
  NotificationType.RansomwareDetectedInResource,
  NotificationType.RansomwareDetectedInSnapshot,
  NotificationType.RansomwareInfectionRemediated,
  NotificationType.ResourceExcludedFromRansomwareDetection,
  NotificationType.RansomwareDetectionExclusionCanceled,
  NotificationType.ViolationsDetectedOnResources,
  NotificationType.ViolationsResolvedOnResources,
  NotificationType.IssueMutedOnResource,
  NotificationType.IssueUnmutedOnResource,
  NotificationType.JobMetricsReportFailed,
  NotificationType.ClassificationJobFailed,
  NotificationType.ClassificationJobRejected,
  NotificationType.ClassificationJobSkipped,
  NotificationType.ClassificationPolicyCreated,
  NotificationType.ClassificationPolicyUpdated,
  NotificationType.ClassificationPolicyDeleted,
  NotificationType.ClassificationPolicyApplied,
  NotificationType.ClassificationPolicyUnapplied,
  NotificationType.CreditsUsageThreshold75,
  NotificationType.CreditsUsageThreshold90,
  NotificationType.CreditsUsageThreshold95,
  NotificationType.CreditsMonthlyUsage80,
  NotificationType.CreditsMonthlyUsage90,
  NotificationType.CreditsMonthlyUsage100,
  NotificationType.MalwareDetectedInSnapshot,
  NotificationType.DataAnomalyDetectedInSnapshot,
  NotificationType.MalwareRemediated,
  NotificationType.DataAnomalyRemediated,
  NotificationType.AiDataRiskDetectedInSnapshot,
  NotificationType.DataExposureRiskDetectedInSnapshot,
  NotificationType.AiDataRiskRemediated,
  NotificationType.DataExposureRiskRemediated,
];

export const gdsgSupportedNotificationTypes = [
  NotificationType.GdsgSlaBreached,
  NotificationType.GdsgSystemInaccessible,
];

export const splunkSupportedNotificationTypes = [
  NotificationType.AuditLogCreated,
  NotificationType.RansomwareDetectedInResource,
  NotificationType.RansomwareDetectedInSnapshot,
  NotificationType.RansomwareInfectionRemediated,
  NotificationType.MalwareDetectedInSnapshot,
  NotificationType.MalwareRemediated,
  NotificationType.DataAnomalyDetectedInSnapshot,
  NotificationType.DataAnomalyRemediated,
  NotificationType.AiDataRiskDetectedInSnapshot,
  NotificationType.AiDataRiskRemediated,
  NotificationType.DataExposureRiskDetectedInSnapshot,
  NotificationType.DataExposureRiskRemediated,
];

interface NotificationTypeGroup {
  label: string;
  types: NotificationType[];
}

export const notificationTypeGroups: NotificationTypeGroup[] = [
  {
    label: 'Backup Jobs',
    types: [
      NotificationType.BackupJobSucceeded,
      NotificationType.BackupJobFailed,
      NotificationType.BackupJobRejected,
      NotificationType.BackupJobSkipped,
    ],
  },
  {
    label: 'Restore Jobs',
    types: [
      NotificationType.RestoreJobSucceeded,
      NotificationType.RestoreJobFailed,
      NotificationType.RestoreJobRejected,
    ],
  },
  {
    label: 'Classification Jobs',
    types: [
      NotificationType.ClassificationJobFailed,
      NotificationType.ClassificationJobRejected,
      NotificationType.ClassificationJobSkipped,
      NotificationType.JobMetricsReportFailed,
    ],
  },
  {
    label: 'Classification Policy',
    types: [
      NotificationType.ClassificationPolicyApplied,
      NotificationType.ClassificationPolicyUnapplied,
      NotificationType.ClassificationPolicyCreated,
      NotificationType.ClassificationPolicyUpdated,
      NotificationType.ClassificationPolicyDeleted,
    ],
  },
  {
    label: 'Backup Policy',
    types: [
      NotificationType.BackupPolicyApplied,
      NotificationType.BackupPolicyUnapplied,
      NotificationType.BackupPolicyCreated,
      NotificationType.BackupPolicyUpdated,
      NotificationType.BackupPolicyDeleted,
    ],
  },
  {
    label: 'Vault',
    types: [
      NotificationType.BackupVaultCreated,
      NotificationType.BackupVaultDeleted,
    ],
  },
  {
    label: 'Cloud Account',
    types: [
      NotificationType.CloudAccountCreated,
      NotificationType.CloudAccountDeactivated,
      NotificationType.CloudAccountActivated,
    ],
  },
  {
    label: 'Ransomware',
    types: [
      NotificationType.RansomwareDetectedInResource,
      NotificationType.RansomwareDetectedInSnapshot,
      NotificationType.RansomwareInfectionRemediated,
      NotificationType.ResourceExcludedFromRansomwareDetection,
      NotificationType.RansomwareDetectionExclusionCanceled,
    ],
  },
  {
    label: 'Malware',
    types: [
      NotificationType.MalwareDetectedInSnapshot,
      NotificationType.MalwareRemediated,
    ],
  },
  {
    label: 'Data Anomaly',
    types: [
      NotificationType.DataAnomalyDetectedInSnapshot,
      NotificationType.DataAnomalyRemediated,
    ],
  },
  {
    label: 'AI data risk',
    types: [
      NotificationType.AiDataRiskDetectedInSnapshot,
      NotificationType.AiDataRiskRemediated,
    ],
  },
  {
    label: 'Data exposure risk',
    types: [
      NotificationType.DataExposureRiskDetectedInSnapshot,
      NotificationType.DataExposureRiskRemediated,
    ],
  },
  {
    label: 'Compliance',
    types: [
      NotificationType.ViolationsDetectedOnResources,
      NotificationType.ViolationsResolvedOnResources,
      NotificationType.IssueMutedOnResource,
      NotificationType.IssueUnmutedOnResource,
    ],
  },
  {
    label: 'Credits',
    types: [
      NotificationType.CreditsUsageThreshold75,
      NotificationType.CreditsUsageThreshold90,
      NotificationType.CreditsUsageThreshold95,
      NotificationType.CreditsMonthlyUsage80,
      NotificationType.CreditsMonthlyUsage90,
      NotificationType.CreditsMonthlyUsage100,
    ],
  },
];

const notificationPolicyTypes: Record<
  NotificationPolicyTypeEnum,
  {
    title: string;
    logo: StaticImageData;
    parametersTitle: string;
    docsUrl: string;
  }
> = {
  [NotificationPolicyTypeEnum.Email]: {
    title: 'Email',
    logo: Email,
    parametersTitle: 'Email details',
    docsUrl: getDocsUrl('notificationPolicyEmail'),
  },
  [NotificationPolicyTypeEnum.Slack]: {
    title: 'Slack',
    logo: Slack,
    parametersTitle: 'Slack app details',
    docsUrl: getDocsUrl('notificationPolicySlack'),
  },
  [NotificationPolicyTypeEnum.Sns]: {
    title: 'Amazon SNS',
    logo: SNS,
    parametersTitle: 'SNS topic details',
    docsUrl: getDocsUrl('notificationPolicySns'),
  },
  [NotificationPolicyTypeEnum.Splunk]: {
    title: 'Splunk',
    logo: Splunk,
    parametersTitle: 'Splunk details',
    docsUrl: getDocsUrl('notificationPolicySplunk'),
  },
  [NotificationPolicyTypeEnum.Webhook]: {
    title: 'Webhook',
    logo: Webhook,
    parametersTitle: 'Webhook details',
    docsUrl: '',
  },
  [NotificationPolicyTypeEnum.ServiceNow]: {
    title: 'ServiceNow',
    logo: ServiceNow,
    parametersTitle: 'ServiceNow details',
    docsUrl: getDocsUrl('notificationPolicyServiceNow'),
  },
  [NotificationPolicyTypeEnum.Datadog]: {
    title: 'Datadog',
    logo: Datadog,
    parametersTitle: 'Datadog details',
    docsUrl: getDocsUrl('notificationPolicyDatadog'),
  },
};

export const NotificationPolicyTypes = Object.fromEntries(
  Object.entries(notificationPolicyTypes)
);
